import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";

// Import professional images
import clientMeetingImage from "@/assets/professional-meeting.jpg";
import remoteWorkSetupImage from "@/assets/team-workspace.jpg";
import remoteWorkOfficeImage from "@/assets/tech-background.jpg";

const Testimonials = () => {
  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Chief Technology Officer",
      company: "Fortune 45 Manufacturing Company",
      image: "src/assets/avatar-placeholder.jpg",
      rating: 5,
      content:
        "Smartborn transformed our legacy infrastructure into a modern, cloud-native platform. Their deep expertise in SAP integration and AI-driven analytics delivered $3.2M in cost savings within the first year. Exceptional technical capabilities and enterprise-grade delivery.",
      project: "Enterprise Cloud Migration & SAP Integration",
    },
    {
      name: "Michael Chen",
      role: "VP of Engineering",
      company: "Global Financial Services",
      image: "src/assets/avatar-placeholder.jpg",
      rating: 5,
      content:
        "The offshore development center Smartborn established has been transformational for our engineering capacity. We scaled from 20 to 120 developers in 6 months with zero quality compromise. Their ODC model is best-in-class.",
      project: "Offshore Development Center - 120 Engineers",
    },
    {
      name: "Emily Rodriguez",
      role: "Director of Digital Innovation",
      company: "Leading Retail Corporation",
      image: "src/assets/avatar-placeholder.jpg",
      rating: 5,
      content:
        "Smartborn's AI-powered mobile platform increased our customer engagement by 300% and drove 45% revenue growth. Their cross-platform expertise and enterprise integration capabilities are outstanding. A true strategic partner.",
      project: "AI-Powered Mobile Commerce Platform",
    },
    {
      name: "David Thompson",
      role: "CISO",
      company: "International Healthcare Network",
      image: "src/assets/avatar-placeholder.jpg",
      rating: 5,
      content:
        "Security and compliance were mission-critical for our patient data systems. Smartborn delivered a comprehensive cybersecurity framework that achieved 100% HIPAA compliance and reduced security incidents by 80%. Exceptional expertise.",
      project: "Enterprise Cybersecurity & Compliance",
    },
    {
      name: "Vadiraj Bilgi",
      role: "CEO",
      company: "Kou-Chan",
      image: "src/assets/avatar-placeholder.jpg",
      rating: 5,
      content:
        "The predictive maintenance platform Smartborn built using AI and IoT reduced our defect rate by 85% and improved production efficiency by 40%. Their machine learning expertise delivered measurable ROI in months, not years.",
      project: "Wallet & Transportation Platform",
    },
    {
      name: "Hariprasad",
      role: "Founder and CEO",
      company: "Vivaan Technology",
      image: "src/assets/Hariprasad.png",
      rating: 5,
      content:
        "Smartborn's DevOps transformation increased our deployment frequency by 10x and reduced lead time by 70%. Their CI/CD expertise and automation capabilities are world-class. They understand enterprise-scale DevOps and delivered impressively dependable results.",
      project: "Manufacturing & DevOps Transformation",
    },
  ];

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="flex items-center gap-1 mb-4">
        {[...Array(fullStars)].map((_, i) => (
          <Star
            key={`full-${i}`}
            // className="h-5 w-5 fill-secondary text-secondary"
            className={`h-5 w-5 ${i % 2 === 0 ? "text-primary shadow-glow fill-primary" : "text-secondary shadow-glow-secondary fill-secondary"}`}
          />
        ))}

        {hasHalfStar && (
          <Star
            key="half"
            className="h-5 w-5 text-warning"
            style={{ fill: "url(#half-gradient)" }}
          />
        )}

        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} className="h-5 w-5 text-primary" />
        ))}

        <svg width="0" height="0">
          <defs>
            <linearGradient id="half-gradient" x1="0" x2="100%" y1="0" y2="0">
              <stop offset="50%" stopColor="hsl(22 100% 50%)" />
              <stop offset="50%" stopColor="transparent" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    );
  };

  return (
    <section
      id="testimonials"
      className="py-12 md:py-16 lg:py-20 bg-gradient-to-b from-background via-secondary/5 to-background relative"
    >
      {/* Background Image */}
      <div className="absolute inset-0 opacity-5">
        <img
          src={clientMeetingImage}
          alt="Client meeting background"
          className="w-full h-full object-cover"
        />
      </div>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 text-foreground bg-duo-gradient bg-clip-text text-transparent pb-2">
            Trusted by Global Enterprises
          </h2>
          <p className="text-lg md:text-xl text-foreground max-w-3xl mx-auto mb-3">
            <strong>Real results from real businesses</strong> that partnered
            with Smartborn
          </p>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            98% client satisfaction · Fortune 45 trusted
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {testimonials.map((testimonial, index) => {
            // Add different background images to different testimonial cards
            const cardImages = [
              remoteWorkSetupImage,
              remoteWorkOfficeImage,
              clientMeetingImage,
            ];
            const cardBgImage = cardImages[index % cardImages.length];

            return (
              <Card
                key={index}
                className="group hover:shadow-elegant transition-all duration-300 hover:-translate-y-1 bg-gradient-card border-0 shadow-card relative overflow-hidden"
              >
                {/* Card Background Image */}
                <div className="absolute inset-0 opacity-5">
                  <img
                    src={cardBgImage}
                    alt="Professional background"
                    className="w-full h-full object-cover"
                  />
                </div>
                <CardContent className="p-8 relative z-10">
                  <div className="absolute top-4 right-4 text-accent/20">
                    <Quote className="h-8 w-8" />
                  </div>

                  {/* <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-warning text-warning" />
                  ))}
                </div> */}

                  {renderStars(testimonial.rating)}

                  <p className="text-body text-foreground mb-6 leading-relaxed italic">
                    "{testimonial.content}"
                  </p>

                  <div className="border-t pt-6">
                    <div className="flex items-center gap-4">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="text-h6 text-foreground">
                          {testimonial.name}
                        </h4>
                        <p className="text-body-sm text-muted-foreground">
                          {testimonial.role}
                        </p>
                        <p className="text-body-sm text-primary font-medium">
                          {testimonial.company}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 text-caption text-accent bg-accent/10 px-2 py-1 rounded-full w-fit">
                      Project: {testimonial.project}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
